import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { useApiClient } from '../../../apiClient';
import {
  CancelCourtDayBodyDto,
  CancelCourtDayResultDto,
} from '../../../models/payment/dto/cancel-court-day.dto';

/** One court's outcome: either the API's per-game result, or a court-level failure. */
export type PerCourtCancelOutcome = {
  courtId: number;
  result?: CancelCourtDayResultDto;
  /** Set when the whole request failed (403, 500, network) — not a per-game failure. */
  error?: string;
};

export type CancelCourtsDayAggregate = {
  perCourt: PerCourtCancelOutcome[];
  /** Games canceled across every court. */
  canceledCount: number;
  /** Per-GAME failures summed across courts. */
  failedCount: number;
  /** Courts whose request never completed. */
  erroredCourtIds: number[];
};

export type CancelCourtsDayVariables = CancelCourtDayBodyDto & {
  courtIds: number[];
};

type Options = {
  onResult?: (aggregate: CancelCourtsDayAggregate) => void;
  onError?: (error: unknown) => void;
  /** Progress ticks for the UI: court `index + 1` of `total` is starting. */
  onProgress?: (started: number, total: number, courtId: number) => void;
};

/**
 * MYS-45: cancel + refund + notify every still-upcoming game on N courts for a
 * day window, in one operator action.
 *
 * One hook for both entry points — the court-details page passes a
 * single-element `courtIds` — so there is exactly one code path for an
 * operation that moves real money.
 *
 * Courts are processed SEQUENTIALLY on purpose. Each court fans out into one
 * refund per game per player against the org's Stripe connected account;
 * running N courts concurrently would multiply that burst and make a partial
 * failure unorderable. Slower, deterministic, debuggable.
 *
 * A court-level failure NEVER aborts the loop — the remaining courts still get
 * cancelled and the failure is reported per court. The mutation only rejects
 * when every court failed, so `onError` unambiguously means "nothing happened".
 *
 * Retrying is safe: the API re-targets only games still UPCOMING.
 */
export const useCancelCourtsDay = ({
  onResult,
  onError,
  onProgress,
}: Options = {}) => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cancelCourtsDay'],
    mutationFn: async ({
      courtIds,
      ...body
    }: CancelCourtsDayVariables): Promise<CancelCourtsDayAggregate> => {
      const perCourt: PerCourtCancelOutcome[] = [];

      for (let i = 0; i < courtIds.length; i += 1) {
        const courtId = courtIds[i];
        onProgress?.(i + 1, courtIds.length, courtId);
        try {
          const result = await apiClient.fetchApi<
            CancelCourtDayResultDto,
            CancelCourtDayBodyDto
          >({
            method: 'POST',
            endpoint: `payments/cancel-court-day/${courtId}`,
            body,
          });
          perCourt.push({ courtId, result });
        } catch (error) {
          perCourt.push({
            courtId,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      }

      const erroredCourtIds = perCourt
        .filter((c) => c.error !== undefined)
        .map((c) => c.courtId);

      // Every court failed → reject, so the caller's error path fires instead of
      // an "all done" result view listing nothing but errors. Guarded on a
      // non-empty selection so an empty request resolves rather than throwing.
      if (courtIds.length > 0 && erroredCourtIds.length === courtIds.length) {
        throw new Error(perCourt[0].error);
      }

      return {
        perCourt,
        canceledCount: perCourt.reduce(
          (sum, c) => sum + (c.result?.canceledCount ?? 0),
          0,
        ),
        failedCount: perCourt.reduce(
          (sum, c) => sum + (c.result?.failedCount ?? 0),
          0,
        ),
        erroredCourtIds,
      };
    },
    onSuccess: (aggregate) => {
      // ONE invalidation for the whole batch. Invalidating per court would fire
      // a prefix match against every court-calendar query the calendar page
      // holds, once per court — N x N refetches on a page showing N courts.
      queryClient.invalidateQueries({ queryKey: ['courtCalendar'] });

      // A partially-failed money operation should be visible without waiting
      // for the operator to report it. Counts and court ids only — never a
      // player id, an email, or an amount.
      if (aggregate.failedCount > 0 || aggregate.erroredCourtIds.length > 0) {
        Sentry.captureMessage('MYS-45 cancel-court-day partial failure', {
          level: 'warning',
          extra: {
            courtCount: aggregate.perCourt.length,
            canceledCount: aggregate.canceledCount,
            failedCount: aggregate.failedCount,
            erroredCourtIds: aggregate.erroredCourtIds,
          },
        });
      }

      onResult?.(aggregate);
    },
    onError,
  });
};
